/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
/* eslint-disable object-curly-newline */

// import libraries
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    string, shape, arrayOf, object, bool, func,
} from 'prop-types';

// import mui components
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';

// import function helpers
import getSiteConfig from '../../utils/getSiteConfig';
import { getBrand } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getFeatureFlags } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import Helper from '../../state/reducers/Helper';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { mmmmDDYYYY } from '../../utils/dateString';
import { isEmpty } from '../../utils/object';

// import components
import DesktopDeliveryImage from './DesktopItemDetail/DesktopDeliveryImage';
import DesktopShippingAddress from './DesktopItemDetail/DesktopShippingAddress';
import DesktopDeliveryDate from './DesktopItemDetail/DesktopDeliveryDate';
import DesktopOrderTracking from './DesktopItemDetail/DesktopOrderTracking';
import DesktopGiftMessage from './DesktopItemDetail/DesktopGiftMessage';
import DesktopProductDetails from './DesktopItemDetail/DesktopProductDetails';
import ModifyOrder from '../OrderDetails/MMO/ModifyOrder';
import LinkOpenChat from '../../common/LinkOpenChat';
import { isStandalone } from '../../../../helpers/common/helper';

const DesktopItemDetail = (props) => {
    const { ROSSOrder, ROSSModifyOrder } = props;
    const dispatch = useDispatch();

    const [mmoOpen, setMmoOpen] = useState(false);
    const [mmoAction, setMmoAction] = useState('');
    const [hideProductAndDeliveryData, setHideProductAndDeliveryData] = useState({
        hideProducts: false,
        hideDeliveryHistory: false,
    });

    const isHDOrderModifiable = useSelector((state) => state.account.isHDOrderModifiableResult);
    const brand = useSelector(getBrand);
    const featureFlags = useSelector(getFeatureFlags);
    const brandID = getSiteConfig('brand-id');

    const firstROSSItem = ROSSOrder?.items?.[0];
    const productBrand = firstROSSItem?.productBrand;

    const isMMOBrand = featureFlags['is-mmo-variant-flag-on'][productBrand];
    const isFEHDEditableEnabled = featureFlags['is-web-frontend-calling-hd-editable'];
    const isROSSItemStatusEnabled = featureFlags['is-ross-item-status-enabled'];
    const IsPendingCancelEnabled = featureFlags['is-pending-cancel-enabled'];

    const ROSSOrderItem = ROSSOrder;
    const { intOrderNumber } = ROSSOrderItem;

    if (isEmpty(ROSSOrder)) return null;

    useEffect(() => {
        const { fetchIsHDOrderEditable } = props;
        const { status } = ROSSOrderItem;
        if (status === 'CONFIRMED' && isFEHDEditableEnabled) { // call fetchIsHDOrderEditable only when isFEHDEditableEnabled flag is on and product in confirmed state
            fetchIsHDOrderEditable(ROSSOrder);
        }
        /* FPT orders in DH */
        //
        // When in CONFIRMED state,
        // the “Delivery Details” section should be collapsed. (FPT DH ONLY)
        // the “Product Details” section should be expanded
        // the “Billing Details” section should be expanded
        //
        // When in IN-TRANSIT state,
        // the “Delivery Details” section should be expanded
        // the “Product Details” section should be collapsed
        // the “Billing Details” section should be collapsed
        //
        // When in DELIVERED state,
        // the “Delivery Details” section should be expanded
        // the “Product Details” section should be expanded
        // the “Billing Details” section should be expanded
        //
        const deliveryStatus = ROSSOrder?.orders?.[0]?.deliveryStatus;
        const type = ROSSOrder?.orders?.[0]?.type;

        if (type === 'FPT' && deliveryStatus === 'W') {
            setHideProductAndDeliveryData({
                hideDeliveryHistory: true,
                hideProducts: false,
            });
        }
        if (type === 'FPT' && deliveryStatus === 'I') {
            setHideProductAndDeliveryData({
                hideDeliveryHistory: false,
                hideProducts: true,
            });
        }
        if (type === 'FPT' && deliveryStatus === 'D') {
            setHideProductAndDeliveryData({
                hideDeliveryHistory: false,
                hideProducts: false,
            });
        }

        // Log orders with no delivery image
        if (ROSSOrder?.type === 'FPT' && ROSSOrder?.status === 'DELIVERED' && !ROSSOrder?.deliveryImageLink) {
            dispatch(trackEvent({
                eventCategory: 'Delivery Image',
                eventAction: `Desktop: Order# ${ROSSOrder?.orderNumber} does not have a delivery image`,
                eventLabel: `${ROSSOrder?.brandCode} | ${ROSSOrder?.orderNumber}`,
            }));
        }

        return () => {
            const closeEditIframe = (event) => {
                if (event.data === 'close') {
                    setMmoOpen(false);
                }
            };
            if (window.addEventListener) {
                window.removeEventListener('message', closeEditIframe, false);
            } else {
                window.detachEvent('onmessage', closeEditIframe);
            }
        };
    }, []);

    const closeModifyOrder = () => {
        setMmoOpen(false);
    };

    const openModifyOrder = async (action) => {
        const { handleRedirectToLogin, loginByRedirect } = props;

        let redirectToLogin = false;

        const isKMSIEnabled = featureFlags['is-keep-me-signed-in-redirect-enabled'];
        if (isKMSIEnabled) {
            // Handle redirect to login if login required on click of cancel order button
            redirectToLogin = await handleRedirectToLogin(featureFlags, loginByRedirect);
        }

        if (!redirectToLogin) {
            setMmoOpen(true);
            setMmoAction(action);
        }
    };

    const errorMesage = (className) => {
        let data = null;
        if (isMMOBrand) {
            if (ROSSModifyOrder === 'false') {
                data = (
                    <div className={`modify-error error-color  bold ${className}`}>
                        This order has already been processed and can no longer be modified.
                    </div>
                );
            }
        }
        return data;
    };

    const toggleDetailView = (stateToChange) => {
        setHideProductAndDeliveryData({
            ...hideProductAndDeliveryData,
            [stateToChange]: !hideProductAndDeliveryData[stateToChange],
        });
    };

    const isModifyEliglbleCheck = (isHDBrand, HDModifiableResponse) => {
        let isRecipientModifiable = true;
        let isCardMsgModifiable = true;
        let isDeliveryDateModifiable = true;

        if (isFEHDEditableEnabled && isHDBrand && !isEmpty(HDModifiableResponse)) { // HD brands check HD Editable Check if isFEHDEditableEnabled flag ON
            isRecipientModifiable = HDModifiableResponse?.[0]?.recipientEnable === 'true';
            isCardMsgModifiable = HDModifiableResponse?.[0]?.cardMsgEnable === 'true';
            isDeliveryDateModifiable = HDModifiableResponse?.[0]?.deliveryDateEnable === 'true';
        } else { // 18F brands use ROSS response
            isRecipientModifiable = ROSSOrder.recipientProfiles?.[0]?.recipientEnable === 'true';
            if (Array.isArray(ROSSOrder.items)) {
                ROSSOrder.items.forEach((item) => {
                    if (item?.deliveryDateEnable !== 'true') {
                        isDeliveryDateModifiable = false;
                    }
                    if (item?.cardMsgEnable !== 'true') {
                        isCardMsgModifiable = false;
                    }
                });
            }
        }
        if (isHDBrand && featureFlags['is-delivery-date-change-disabled']) isDeliveryDateModifiable = false;
        return {
            isRecipientModifiable,
            isCardMsgModifiable,
            isDeliveryDateModifiable,
        };
    };

    const getMMODefaultTab = (eligibilityObject = {}) => {
        const { isRecipientModifiable, isDeliveryDateModifiable, isCardMsgModifiable } = eligibilityObject;
        /* Returns in order. No need to check deliveryDate and cardMessage if recipient can be modified. */
        if (isRecipientModifiable === true) return 'editAddress';
        if (isDeliveryDateModifiable === true) return 'editDate';
        if (isCardMsgModifiable === true) return 'editMsg';
        return 'editAddress';
    };

    const orderCannotBeModifiedMessage = () => {
        const { isLoadedFromTopic } = props;
        if (isLoadedFromTopic || isStandalone) return <></>;
        return (
            <div className="modify-error desktop-modify-error">
                If you need to make a change to your order, please <LinkOpenChat>chat with us.</LinkOpenChat>
            </div>
        );
    };

    const fullTemplate = () => {
        const { ROSSOrder, isMultiItemOrder, ROSSOrderNumber,
            handleRedirectToLogin, loginByRedirect, isHDBrand,
            ROSSOrderReplacementFlag, ROSSOrderPartialCreditFlag, ROSSCSIFailureReason,
            isShowingTrackingWidget, ROSSOrderTotal, isOrderContainsFoodProduct, deliveryImageFlags } = props;

        const ENABLE_MMO = featureFlags['is-account-mmo-enabled'];
        const isDeliveryImageLinkEnabled = featureFlags['is-fpt-delivery-link-enabled'];

        const hideDeliveryHistory = hideProductAndDeliveryData.hideDeliveryHistory;
        const { hideProducts } = hideProductAndDeliveryData;
        const deliveryImageLink = ROSSOrder?.deliveryImageLink || '';
        const passedVisionTest = ROSSOrderItem?.passedVision || false;
        const shouldDisplayDeliveryImage = isDeliveryImageLinkEnabled && deliveryImageLink !== '' && passedVisionTest; // use for rendering delivery image component
        const passportRegex = /(passport annual membership|passport for|passport at)/gi;
        const isHDBrandProduct = isHDBrand(productBrand);
        const isROSSPassport = firstROSSItem?.subscriptionType === 'Passport' || firstROSSItem?.productName.match(passportRegex)?.length > 0;
        const isDonationItem = Helper.checkIsDonationItem(firstROSSItem?.productName);
        const ROSSCreatedDate = ROSSOrder?.history?.[0]?.createdDate;
        const ROSSOrderDeliveryDate = mmmmDDYYYY(ROSSCreatedDate);
        const ROSSOrderDeliveryTime = ROSSCreatedDate?.substring(ROSSCreatedDate.length - 8, ROSSCreatedDate.length - 3);
        const ROSSOrderDeliveryTimeFormat = ROSSOrderDeliveryTime?.substring(0, 2) > 12 ? `${ROSSOrderDeliveryTime.substring(0, 2) - 12}${ROSSOrderDeliveryTime.substring(2, 6)} PM` : `${ROSSOrderDeliveryTime} AM`;
        const ROSSModifyOrderCondition = (ROSSOrder?.modifyOrder === 'true') && !ROSSOrder?.pendingUpdate;        // isHDOrderModifiable = [ {...}, {...}, {...} ] this is the aggrgated response for ALL the products calling HD edtiable check
        const HDModifiableResponse = Array.isArray(isHDOrderModifiable) && !isEmpty(isHDOrderModifiable)
            // we filter the array with all the responses for the one that matches this product's intOrderNumber
            ? isHDOrderModifiable.filter((response) => response?.intOrderNumber === intOrderNumber)
            : [];
        const recipientSeqNumber = firstROSSItem?.recipientSeqNumber || '';
        const HDModifyOrderCondition = HDModifiableResponse?.[0]?.modifyOrder === 'true';
        const modifyEligibility = isModifyEliglbleCheck(isHDBrandProduct, HDModifiableResponse);
        const isROSSOrderModifiable = isFEHDEditableEnabled && isHDBrandProduct
            ? ROSSModifyOrderCondition && HDModifyOrderCondition // HD brand orders must call an editable check endpoint if isFEHDEditableEnabled flag is ON
            : ROSSModifyOrderCondition;
        const historyUpdateList = [];
        const shouldRenderMMOBtn = () => {
            const { isRecipientModifiable, isCardMsgModifiable, isDeliveryDateModifiable } = modifyEligibility;
            if (isRecipientModifiable === false && isCardMsgModifiable === false && isDeliveryDateModifiable === false) {
                return false; // if all three tabs are false do not render button
            }
            if (ENABLE_MMO && isMMOBrand && !isDonationItem && !isROSSPassport) {
                return isROSSOrderModifiable || false;
            }
            return false;
        };

        return (
            <>
                <Grid container>
                    {!isROSSItemStatusEnabled
                        && (
                            <DesktopOrderTracking
                                isROSSItemStatusEnabled={isROSSItemStatusEnabled}
                                IsPendingCancelEnabled={IsPendingCancelEnabled}
                                ROSSOrder={ROSSOrder}
                                isROSSPassport={isROSSPassport}
                                isDonationItem={isDonationItem}
                            />
                        )}
                    {(ENABLE_MMO && mmoOpen) && (
                        <Grid item xs={12}>
                            <ModifyOrder
                                action={mmoAction}
                                mmoOpen={mmoOpen}
                                closeMMO={closeModifyOrder}
                                ROSSOrder={ROSSOrder}
                                intOrderNumber={intOrderNumber}
                                ROSSModifyOrder={ROSSModifyOrder}
                                recipientSeqNumber={recipientSeqNumber}
                                promCodeFlag={featureFlags['is-new-prom-code-enabled'] || false}
                                modifyEligibility={modifyEligibility}
                                ROSSOrderTotal={ROSSOrderTotal}
                            />
                        </Grid>
                    )}

                    {ROSSOrder.type === 'FPT' && !isEmpty(ROSSOrder?.history) && (
                        <>
                            <div
                                className="toggle-product-button dark-border gray-bg"
                                onKeyDown={() => { }}
                                role="button"
                                tabIndex={-1}
                                onClick={() => toggleDetailView('hideDeliveryHistory')}
                            >
                                <span>{`${hideDeliveryHistory ? 'Expand' : 'Collapse'} Delivery Details`}</span>
                                <img alt="collapse" src={hideDeliveryHistory ? 'https://images.contentstack.io/v3/assets/blt95e0138311135214/blt8434534d6387623f/5fe39f2e396e65084a9e7118/down-arrow.svg' : 'https://images.contentstack.io/v3/assets/blt95e0138311135214/blt683a6cec1ac5e736/5fe39f45e7fdff751b793746/up-arrow.svg'} />
                            </div>
                            {!hideDeliveryHistory && (
                                <>
                                    <Grid container className="history-container bottom-border">
                                        <Grid className="history-titles" item xs={4}> <p className="bold gray-color">FLORIST UPDATES</p> </Grid>
                                        <Grid item xs={4}> <p className="bold gray-color">DATE</p> </Grid>
                                        <Grid item xs={4}> <p className="bold gray-color">ACTIVITY</p> </Grid>
                                        {shouldDisplayDeliveryImage && (
                                            <Grid item xs={12} className="bottom-border margin-bottom">
                                                <DesktopDeliveryImage
                                                    ROSSOrderDeliveryTimeFormat={ROSSOrderDeliveryTimeFormat}
                                                    ROSSOrderDeliveryDate={ROSSOrderDeliveryDate}
                                                    deliveryImageLink={deliveryImageLink}
                                                    orderNumber={ROSSOrderNumber}
                                                    brand={brand?.domain}
                                                    productName={firstROSSItem?.productName}
                                                    productSku={firstROSSItem?.productSku}
                                                    deliveryImageFlags={deliveryImageFlags}
                                                    recipientProfile={ROSSOrder?.recipientProfiles?.[0]}
                                                    trackingNumber={ROSSOrder?.trackingNumber}
                                                />
                                            </Grid>
                                        )}
                                        {ROSSOrder.history.map((update, i) => {
                                            const { status: updateStatus, createdDate, statusDescription } = update;
                                            const timeStamp = mmmmDDYYYY(createdDate);
                                            const updateTime = createdDate.substring(createdDate.length - 8, createdDate.length - 3);
                                            const timeString = updateTime.substring(0, 2) > 12 ? `${updateTime.substring(0, 2) - 12}${updateTime.substring(2, 6)} PM` : `${updateTime} AM`;
                                            if (!updateStatus || (!isEmpty(historyUpdateList) && historyUpdateList.indexOf(updateStatus) !== -1)) return '';
                                            historyUpdateList.push(updateStatus);
                                            return (
                                                <Grid container xs={12} className="history-update" key={createdDate}>
                                                    <Grid container item xs={4}>
                                                        <Grid className="history-img-container">
                                                            <img
                                                                alt="circle"
                                                                src="https://images.contentstack.io/v3/assets/blt95e0138311135214/blt2c9c71d438f8eea1/606f333e5a02884cf97afc64/checked-circle.svg"
                                                            />
                                                            {i !== ROSSOrder.history.length - 1 && (
                                                                <img
                                                                    className="line-img"
                                                                    alt="line"
                                                                    src="https://images.contentstack.io/v3/assets/blt95e0138311135214/blt39f326c7aa2b0d2f/606f333e1609c821964d9c87/connecting-line.svg"
                                                                />
                                                            )}
                                                        </Grid>
                                                        <p className="bold">{updateStatus.toLowerCase()}</p>
                                                    </Grid>
                                                    <Grid item xs={4}> <p>{timeStamp} {timeString}</p> </Grid>
                                                    <Grid item xs={4}> <p>{statusDescription}</p> </Grid>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </>
                            )}
                        </>
                    )}

                    <Grid container className="desktop-shipping bottom-border">
                        <Grid item xs={4}>
                            <DesktopShippingAddress
                                isROSSPassport={isROSSPassport}
                                ROSSRecipient={ROSSOrder?.recipientProfiles?.[0]}
                            />
                        </Grid>
                        {!isDonationItem ? (
                            <>
                                <Grid item xs={4}>
                                    <DesktopDeliveryDate
                                        ROSSOrder={ROSSOrder}
                                        isROSSPassport={isROSSPassport}
                                    />
                                    {
                                        shouldRenderMMOBtn()
                                            ? (
                                                <div style={{ marginTop: '10vh' }}>
                                                    <Button
                                                        className="float-none button18FSquare"
                                                        onClick={() => {
                                                            dispatch(trackEvent({
                                                                eventCategory: 'Order',
                                                                eventAction: 'Modify',
                                                                eventLabel: ROSSOrder?.orderNumber || '',
                                                            }));
                                                            const defaultTab = getMMODefaultTab(modifyEligibility);
                                                            openModifyOrder(defaultTab);
                                                        }}
                                                    >
                                                        Modify
                                                    </Button>
                                                </div>
                                            ) : (orderCannotBeModifiedMessage())
                                    }
                                </Grid>
                                <Grid item xs={4}>
                                    {!isROSSPassport ? (
                                        <>
                                            <DesktopGiftMessage
                                                ROSSItemList={ROSSOrder?.items}
                                                isAccountMMOEnabled={featureFlags['is-account-mmo-enabled']}
                                            />

                                        </>
                                    ) : (
                                        ''
                                    )}
                                </Grid>
                            </>
                        ) : isDonationItem && (
                            errorMesage('desktop-modify-error desktop-modify-error-donation')
                        )}
                    </Grid>

                    <div
                        className="toggle-product-button dark-border gray-bg"
                        onKeyDown={() => { }}
                        role="button"
                        tabIndex={-1}
                        onClick={() => toggleDetailView('hideProducts')}
                    >
                        <span>{`${hideProducts ? 'Expand' : 'Collapse'} Product Details`}</span>
                        <img alt="collapse" src={hideProducts ? 'https://images.contentstack.io/v3/assets/blt95e0138311135214/blt8434534d6387623f/5fe39f2e396e65084a9e7118/down-arrow.svg' : 'https://images.contentstack.io/v3/assets/blt95e0138311135214/blt683a6cec1ac5e736/5fe39f45e7fdff751b793746/up-arrow.svg'} />
                    </div>

                    <Grid className={`desktop-products bottom-border ${hideProducts ? 'hidden' : ''}`} item xs={12}>
                        <DesktopProductDetails
                            ROSSOrder={ROSSOrder}
                            isMultiItemOrder={isMultiItemOrder}
                            ROSSOrderNumber={ROSSOrderNumber}
                            intOrderNumber={intOrderNumber}
                            ROSSOrderPartialCreditFlag={ROSSOrderPartialCreditFlag}
                            ROSSOrderReplacementFlag={ROSSOrderReplacementFlag}
                            ROSSCSIFailureReason={ROSSCSIFailureReason}
                            isROSSPassport={isROSSPassport}
                            isDonationItem={isDonationItem}
                            handleRedirectToLogin={handleRedirectToLogin}
                            loginByRedirect={loginByRedirect}
                            brandID={brandID}
                            isShowingTrackingWidget={isShowingTrackingWidget}
                            isOrderContainsFoodProduct={isOrderContainsFoodProduct}
                            ROSSOrderTotal={ROSSOrderTotal}
                        />
                    </Grid>
                </Grid>
            </>
        );
    };

    return (
        <Card className="margin-top delivery-info">
            <CardContent className="desktop-item-detail">
                {fullTemplate()}
            </CardContent>
        </Card>
    );
};

DesktopItemDetail.defaultProps = {
    ROSSOrder: {},
    ROSSOrderNumber: '',
};

DesktopItemDetail.propTypes = {
    ROSSOrder: shape({
        brandId: string,
        modifiedDateTime: string,
        orderNumber: string,
        orders: arrayOf(shape({
            actualDeliveryDate: string,
            address: string,
            carrier: string,
            courier: string,
            createdDate: string,
            currentLocation: object,
            deliveryDate: string,
            deliveryImageURL: string,
            deliveryMethod: string,
            deliveryNotes: string,
            history: arrayOf(shape({
                codeStatus: string,
                createdDate: string,
                location: object,
                status: string,
                statusDescription: string,
            })),
            intOrderNumber: string,
        })),
        items: arrayOf(shape({
            productBrand: string,
            itemType: string,
            productName: string,
            productSku: string,
        })),
        origin: string,
        recipientFirstName: string,
        recipientLastName: string,
        signatureImageURL: string,
        status: string,
        statusDescription: string,
        trackingNumber: string,
        type: string,
        warningMessage: string,
    }),
    isMultiItemOrder: bool.isRequired,
    ROSSOrderReplacementFlag: bool.isRequired,
    ROSSOrderPartialCreditFlag: bool.isRequired,
    ROSSOrderNumber: string,
    ROSSModifyOrder: string.isRequired,
    handleRedirectToLogin: func.isRequired,
    loginByRedirect: func.isRequired,
    isHDBrand: func.isRequired,
    ROSSCSIFailureReason: object.isRequired,
    fetchIsHDOrderEditable: func.isRequired,
    isShowingTrackingWidget: func.isRequired,
    ROSSOrderTotal: string.isRequired,
    isOrderContainsFoodProduct: bool.isRequired,
};

export default DesktopItemDetail;
